<script setup lang="ts">
const meta = inject('meta', ref())

const status: string = 'PUBLISHED'

const route = useRoute()

const page = ref(parseInt(route.query.p || 1))
const strapiData = ref(null)

const { public: config } = useRuntimeConfig()
const locale = config.strapiLocale
const { $t } = useI18n()

async function fetchData() {
  const { data: response } = await useAsyncGraphqlQuery('blogOverview', {
    documentId: meta.value.strapi_id,
    locale,
    status,
    page: page.value,
  }, useGraphqlDefaultClient())

  strapiData.value = response?.value?.data
}

await fetchData()

watch(() => route.query.p, async (p) => {
  page.value = parseInt(p || 1)
  await fetchData()
})

const blogOverviews = computed(() => strapiData.value?.blogOverviews?.filter((category: Record<string, unknown>) => !!category) || [])
const blogPages = computed(() => strapiData.value?.blogPages?.nodes?.filter((blog: Record<string, unknown>) => blog) || [])
const recentBlogs = computed(() => strapiData.value?.recent?.filter((blog: Record<string, unknown>) => blog) || [])

const pageTitle = computed(
  () => strapiData.value?.blogOverview?.PageTitle || '',
)

const metaTitle = computed(
  () => strapiData.value?.blogOverview?.MetaData?.MetaTitle || pageTitle.value + ' | ' + $t('meta.title'),
)

const metaDescription = computed(
  () => strapiData.value?.blogOverview?.MetaData?.MetaDescription || $t('meta.description'),
)

const { setBreadcrumbs } = useBreadcrumbs()
setBreadcrumbs({
  label: pageTitle.value,
  parents: meta.value.parents,
})

const { pageView } = useDataLayer()
pageView('blog-overview', metaTitle.value)

const updatePage = (newPage: number) => {
  const query = { ...route.query }
  if (newPage === 1) {
    delete query.p
  }
  else {
    query.p = newPage.toString()
  }
  navigateTo({ query })
}

useHead({
  title: metaTitle,
  meta: [
    { name: 'description', content: metaDescription },
    { name: 'robots', content: strapiData.value?.blogOverview?.MetaData?.MetaRobots?.replace('_', ', ') },
  ],
})

const { canonical } = useSeo()
canonical(computed(() => route.path))
</script>

<template>
  <div class="blog-overview">
    <AppBreadcrumbs v-if="$route.path !== '/'" />

    <StrapiSlice
      :slice="{
        Settings: {
          Title: strapiData?.blogOverview?.PageTitle || '',
          SliceWidth: 'large-column',
          Title2: strapiData?.blogOverview?.Description || '',
          TitleStyle: 'All_Caps_Handwritten',
        },
      }"
    >
      <div
        v-if="blogOverviews?.length"
        class="blog-overview__blog-categories"
      >
        <BlogCategory
          v-for="(category, index) in blogOverviews"
          :key="category"
          :category="category"
          :lazy="index > 2"
        />
      </div>

      <template v-if="blogPages?.length">
        <div
          class="blog-overview__blog-grid"
        >
          <BlogTile
            v-for="(blog, index) in blogPages"
            :key="blog"
            :blog="blog"
            :lazy="index > 2 && !blogOverviews?.length"
          />
        </div>

        <PaginatorPages
          v-if="strapiData?.blogPages?.pageInfo?.pageCount > 1"
          class="blog-overview__blog-pages"
          :current-page="strapiData?.blogPages?.pageInfo?.page"
          :total-pages="strapiData?.blogPages?.pageInfo?.pageCount"
          @update-page="updatePage"
        />
      </template>
    </StrapiSlice>

    <StrapiSlice
      v-if="!blogPages?.length && recentBlogs?.length"
      :slice="{
        Settings: {
          Title: 'Recent verschenen',
          SliceWidth: 'large-column',
          TitleStyle: 'All_Caps_Handwritten',
        },
      }"
    >
      <div
        class="blog-overview__blog-grid"
      >
        <BlogTile
          v-for="blog in recentBlogs"
          :key="blog"
          :blog="blog"
          :lazy="false"
        />
      </div>
    </StrapiSlice>
  </div>
</template>

<style>
@import url("~layers/app/pages/~blog-overview.css");
</style>
